import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

export interface CreateUserFormProps {
  /** Omit page chrome; use inside a dialog */
  embedded?: boolean;
  onCreated?: () => void;
}

function namesFromDisplayName(displayName: string): { firstName: string; lastName: string } {
  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    return { firstName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0]!, lastName: parts[0]! };
  }
  return { firstName: parts[0]!, lastName: parts.slice(1).join(" ") };
}

export function CreateUserForm({ embedded = false, onCreated }: CreateUserFormProps) {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const orgsQuery = useQuery({
    queryKey: ["user-admin", "orgs"],
    queryFn: () => apiFetch<{ orgs: { id: number; name: string | null }[] }>("/api/user-admin/orgs"),
  });
  const rolesQuery = useQuery({
    queryKey: ["user-admin", "roles"],
    queryFn: () => apiFetch<{ roles: { id: number; name: string | null }[] }>("/api/user-admin/roles"),
  });

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [orgId, setOrgId] = useState<number | "">("");
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [fsUserId, setFsUserId] = useState("");
  const [createStaff, setCreateStaff] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [staffActive, setStaffActive] = useState(true);
  const [staffFsUserId, setStaffFsUserId] = useState("");

  const derivedStaffNames = namesFromDisplayName(displayName);
  const staffFirstName = firstName.trim() || derivedStaffNames.firstName;
  const staffLastName = lastName.trim() || derivedStaffNames.lastName;

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{
        userId: string;
        email: string;
        message: string;
      }>("/api/user-admin/users", {
        method: "POST",
        body: {
          email,
          emailConfirm: true,
          displayName: displayName.trim() || null,
          orgId: orgId === "" ? undefined : orgId,
          roleIds: roleIds.length > 0 ? roleIds : undefined,
          fsUserId: fsUserId.trim() || null,
          staff: createStaff
            ? {
                firstName: staffFirstName,
                lastName: staffLastName,
                active: staffActive,
                fsUserId: staffFsUserId.trim() || null,
              }
            : undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "directory"] });
      setEmail("");
      setDisplayName("");
      setFirstName("");
      setLastName("");
      setRoleIds([]);
      onCreated?.();
    },
  });

  const orgs = orgsQuery.data?.orgs ?? [];
  const roles = rolesQuery.data?.roles ?? [];

  useEffect(() => {
    if (orgId !== "" || orgs.length === 0) return;
    if (orgs.some((organisation) => organisation.id === 1)) {
      setOrgId(1);
    }
  }, [orgs, orgId]);

  const formBody = (
    <CardBody className="space-y-4">
      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
      <fieldset
        disabled={!hubWritesAllowed || createMutation.isPending}
        className="min-w-0 space-y-4 border-0 p-0"
      >
      <p className="text-sm text-slate-600">
        Sign-in uses Google Workspace single sign-on. The hub stores a random password that is never emailed; the
        person can use the password-reset flow only if they need an email and password fallback.
      </p>
      <div>
        <Label htmlFor="create-email">Work email</Label>
        <Input
          id="create-email"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mt-1"
          placeholder="name@smartgridsolutions.com"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="create-display">Display name</Label>
          <Input
            id="create-display"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className="mt-1"
            placeholder="Optional; must be unique if set"
          />
        </div>
        <div>
          <Label htmlFor="create-fs">Freshsales user id</Label>
          <Input
            id="create-fs"
            value={fsUserId}
            onChange={(event) => setFsUserId(event.target.value)}
            className="mt-1 font-mono text-xs"
            placeholder="Optional numeric id"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="create-org">Organisation</Label>
          <select
            id="create-org"
            value={orgId === "" ? "" : String(orgId)}
            onChange={(event) => {
              const value = event.target.value;
              setOrgId(value === "" ? "" : Number.parseInt(value, 10));
            }}
            className={cn(
              "mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900",
              "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
            )}
          >
            <option value="">Select organisation</option>
            {orgs.map((organisation) => (
              <option key={organisation.id} value={organisation.id}>
                {organisation.name ?? `Org ${organisation.id}`}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <span className="text-sm font-medium text-slate-900">Roles</span>
          <p className="mt-1 text-xs text-slate-500">
            Optional. Admin is full hub access; leave it unchecked unless this person should be an integrator
            administrator.
          </p>
          <div className="mt-2 flex flex-col gap-2 rounded-md border border-slate-200 p-3">
            {roles.map((role) => (
              <label key={role.id} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={roleIds.includes(role.id)}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setRoleIds((previous) => [...previous, role.id].sort((left, right) => left - right));
                    } else {
                      setRoleIds((previous) => previous.filter((id) => id !== role.id));
                    }
                  }}
                  className="rounded border-slate-300"
                />
                {role.name ?? `Role ${role.id}`}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 p-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <input
            type="checkbox"
            checked={createStaff}
            onChange={(event) => setCreateStaff(event.target.checked)}
            className="rounded border-slate-300"
          />
          Create matching employee directory (<code className="text-xs font-normal">sgs.staff</code>) row
        </label>
        {createStaff ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="staff-first">First name</Label>
              <Input
                id="staff-first"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className="mt-1"
                placeholder={derivedStaffNames.firstName || undefined}
              />
            </div>
            <div>
              <Label htmlFor="staff-last">Last name</Label>
              <Input
                id="staff-last"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className="mt-1"
                placeholder={derivedStaffNames.lastName || undefined}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={staffActive}
                onChange={(event) => setStaffActive(event.target.checked)}
                className="rounded border-slate-300"
              />
              Active
            </label>
            <div className="sm:col-span-2">
              <Label htmlFor="staff-fs">Staff Freshsales user id (optional)</Label>
              <Input
                id="staff-fs"
                value={staffFsUserId}
                onChange={(event) => setStaffFsUserId(event.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
          </div>
        ) : null}
      </div>
      </fieldset>
      </ReadOnlyHoverLayer>

      <div className="flex flex-wrap items-center gap-3">
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
        <Button
          onClick={() => createMutation.mutate()}
          isLoading={createMutation.isPending}
          disabled={
            orgId === "" ||
            !email.trim() ||
            !hubWritesAllowed ||
            (createStaff && (!staffFirstName || !staffLastName))
          }
        >
          Create user
        </Button>
        </ReadOnlyHoverLayer>
        {createMutation.isSuccess ? (
          <p className="text-sm text-emerald-700">
            {embedded
              ? `Created ${createMutation.data.email}.`
              : (
                  <>
                    Created {createMutation.data.email} — id <code className="text-xs">{createMutation.data.userId}</code>
                  </>
                )}
          </p>
        ) : null}
      </div>

      {createMutation.error ? (
        <p className="text-sm text-rose-700">{(createMutation.error as Error).message}</p>
      ) : null}
    </CardBody>
  );

  if (embedded) {
    return (
      <Card className="border-0 shadow-none">
        {formBody}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Create user</h1>
        <p className="text-sm text-slate-600">
          Provisions a Supabase Auth account for Google Workspace single sign-on (the database trigger also inserts a minimal{" "}
          <code className="text-xs">public.user_setting</code> row). This form then sets organisation, role, and optional
          fields, and can add an <code className="text-xs">sgs.staff</code> employee-directory row linked by email and{" "}
          <code className="text-xs">sb_user_id</code>.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>New sign-in</CardTitle>
        </CardHeader>
        {formBody}
      </Card>
    </div>
  );
}
